/**
 * Articles API service — Phase 3
 * Typed wrappers around the knowledge management REST API.
 */

import apiClient from './client';

// ── Types ────────────────────────────────────────────────────────────────────

export type ComplexityLevel = 'L100' | 'L200' | 'L300' | 'L400' | 'L500';
export type ContentType =
  | 'Sales Play'
  | 'Best Practice'
  | 'Customer Success Story'
  | 'Technical How-To'
  | 'Troubleshooting Guide'
  | 'Feature Overview'
  | 'Integration Guide'
  | 'FAQ';
export type AudienceType = 'Internal' | 'External';
export type SortOption = 'relevance' | 'date' | 'popularity';

export interface ArticleSummary {
  id: string;
  articleId?: string;
  title: string;
  summary: string;
  authorId: string;
  authorName: string;
  complexityLevel: ComplexityLevel;
  domainTags: string[];
  contentType: ContentType;
  audienceType: AudienceType;
  ndaRequired: boolean;
  viewCount: number;
  bookmarked: boolean;
  updatedAt: string;
  createdAt: string;
}

export interface ArticleDetail extends ArticleSummary {
  content: string; // markdown
  domain: string;
  topicId?: string;
  subTopicId?: string;
  relatedArticleIds?: string[];
  lLevelHistory?: { from: string; to: string; changedBy: string; changedAt: string }[];
}

export interface SearchParams {
  q?: string;
  contentType?: ContentType;
  complexityLevel?: ComplexityLevel;
  domainTags?: string;
  sort?: SortOption;
  page?: number;
  limit?: number;
}

export interface SearchResponse {
  articles: ArticleSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface RelatedArticle {
  id: string;
  title: string;
  complexityLevel: ComplexityLevel;
  domainTags: string[];
}

export type UploadStatus = 'uploading' | 'processing' | 'completed' | 'failed';

export interface UploadStatusResponse {
  uploadId: string;
  status: UploadStatus;
  progress: number;
  extractedContent?: string;
  extractedTitle?: string;
  extractedAuthor?: string;
  extractedDate?: string;
  extractedImages?: string[];
  piiDetected?: boolean;
  piiDetails?: string[];
  error?: string;
}

export interface Attachment {
  attachmentId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface ArticleCreatePayload {
  title: string;
  summary: string;
  content: string;
  contentType?: ContentType;
  complexityLevel?: ComplexityLevel;
  audienceType?: AudienceType;
  ndaRequired?: boolean;
  domainTags?: string[];
  domainId?: string;
  topicId?: string;
  subTopicId?: string;
}

export interface ArticleUpdatePayload extends Partial<ArticleCreatePayload> {}

export interface TaxonomyDomain {
  domainId: string;
  domainName: string;
  domainNumber: string;
  barRaiserAlias?: string;
  barRaiserGroupName?: string;
  topicCount?: number;
}

export interface TaxonomyTopic {
  topicId: string;
  topicName: string;
  topicNumber: string;
  parentTopicId?: string | null;
  delegateChampionAlias?: string | null;
}

export interface TaxonomySubTopic {
  subTopicId: string;
  subTopicName: string;
  subTopicNumber: string;
}

export interface LLevelGuide {
  levels: {
    level: ComplexityLevel;
    name: string;
    description: string;
    criteria: string[];
    example: string;
    nextLevelHint: string;
  }[];
  note: string;
}

export interface DuplicateCandidate {
  articleId: string;
  title: string;
  summary: string;
  similarityScore: number;
  complexityLevel: ComplexityLevel;
}

export interface AIAssessment {
  suggestedLevel: ComplexityLevel;
  reasoning: string;
  toReachNextLevel: string[];
  improvements: string[];
}

// ── API methods ───────────────────────────────────────────────────────────────

export const articlesApi = {
  // Search
  search(params: SearchParams): Promise<SearchResponse> {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    );
    return apiClient.get<SearchResponse>('/api/v1/search', { params: clean }).then((r) => {
      const raw = r.data as any;
      // The search Lambda returns { results, total, offset, limit } with fields like
      // articleId, author, lastModifiedDate, createdDate. The frontend ArticleSummary
      // expects id, authorName, updatedAt, createdAt. Map the response fields.
      return {
        articles: (raw.results ?? raw.articles ?? []).map((a: any) => ({
          ...a,
          id: a.id ?? a.articleId,
          authorId: a.authorId ?? a.author ?? '',
          authorName: a.authorName ?? a.author ?? '',
          updatedAt: a.updatedAt ?? a.lastModifiedDate ?? '',
          createdAt: a.createdAt ?? a.createdDate ?? '',
          bookmarked: a.bookmarked ?? false,
          viewCount: a.viewCount ?? 0,
          audienceType: a.audienceType ?? 'Internal',
          ndaRequired: a.ndaRequired ?? false,
        })),
        total: raw.total ?? 0,
        page: raw.page ?? (raw.offset != null ? Math.floor(raw.offset / (raw.limit || 20)) + 1 : 1),
        limit: raw.limit ?? 20,
      };
    });
  },

  // Article CRUD
  getById(articleId: string): Promise<ArticleDetail> {
    return apiClient.get<ArticleDetail>(`/api/v1/articles/${articleId}`).then((r) => r.data);
  },

  getRelated(articleId: string): Promise<RelatedArticle[]> {
    return apiClient
      .get<{ articles: RelatedArticle[] }>(`/api/v1/articles/${articleId}/related`)
      .then((r) => r.data.articles);
  },

  createDraft(payload: ArticleCreatePayload): Promise<ArticleDetail> {
    return apiClient.post<ArticleDetail>('/api/v1/articles', payload).then((r) => r.data);
  },

  updateDraft(articleId: string, payload: ArticleUpdatePayload): Promise<ArticleDetail> {
    return apiClient.put<ArticleDetail>(`/api/v1/articles/${articleId}`, payload).then((r) => r.data);
  },

  submitForReview(articleId: string): Promise<void> {
    return apiClient.post(`/api/v1/articles/${articleId}/submit`).then(() => undefined);
  },

  deleteArticle(articleId: string): Promise<void> {
    return apiClient.delete(`/api/v1/articles/${articleId}`).then(() => undefined);
  },

  publishArticle(articleId: string): Promise<void> {
    return apiClient.post(`/api/v1/articles/${articleId}/publish`).then(() => undefined);
  },

  // Bookmarks & feedback
  bookmark(articleId: string): Promise<void> {
    return apiClient.post(`/api/v1/articles/${articleId}/bookmark`).then(() => undefined);
  },

  unbookmark(articleId: string): Promise<void> {
    return apiClient.delete(`/api/v1/articles/${articleId}/bookmark`).then(() => undefined);
  },

  submitFeedback(articleId: string, helpful: boolean): Promise<void> {
    return apiClient.post(`/api/v1/articles/${articleId}/feedback`, { helpful }).then(() => undefined);
  },

  // Upload (4.6.1 / 4.6.5)
  uploadDocument(file: File, onProgress?: (pct: number) => void): Promise<{ uploadId: string }> {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<{ uploadId: string }>('/api/v1/articles/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then((r) => r.data);
  },

  getUploadStatus(uploadId: string): Promise<UploadStatusResponse> {
    return apiClient
      .get<UploadStatusResponse>(`/api/v1/articles/upload/${uploadId}/status`)
      .then((r) => r.data);
  },

  listAttachments(articleId: string): Promise<Attachment[]> {
    return apiClient
      .get<{ attachments: Attachment[] }>(`/api/v1/articles/${articleId}/attachments`)
      .then((r) => r.data.attachments);
  },

  downloadAttachment(articleId: string, attachmentId: string): Promise<string> {
    return apiClient
      .get<{ url: string }>(`/api/v1/articles/${articleId}/attachments/${attachmentId}`)
      .then((r) => r.data.url);
  },

  // Duplicate detection (4.3.1)
  checkDuplicates(title: string, content: string): Promise<DuplicateCandidate[]> {
    return apiClient
      .post<{ duplicates: DuplicateCandidate[] }>('/api/v1/articles/check-duplicates', { title, content })
      .then((r) => r.data.duplicates);
  },

  // AI L-level assessment (4.7.3)
  assessLLevel(articleId: string): Promise<AIAssessment> {
    return apiClient
      .post<{ articleId: string; assessment: AIAssessment }>(`/api/v1/articles/${articleId}/assess-l-level`)
      .then((r) => r.data.assessment ?? (r.data as unknown as AIAssessment));
  },

  // Taxonomy (4.7.4)
  getTaxonomyDomains(): Promise<TaxonomyDomain[]> {
    return apiClient
      .get<{ domains: TaxonomyDomain[] }>('/api/v1/taxonomy/services/amazon-connect/domains')
      .then((r) => r.data.domains);
  },

  getTaxonomyTopics(domainId: string): Promise<TaxonomyTopic[]> {
    return apiClient
      .get<{ topics: TaxonomyTopic[] }>(`/api/v1/taxonomy/domains/${domainId}/topics`)
      .then((r) => r.data.topics);
  },

  getTaxonomySubTopics(domainId: string, topicId: string): Promise<TaxonomySubTopic[]> {
    return apiClient
      .get<{ subTopics: TaxonomySubTopic[] }>(`/api/v1/taxonomy/domains/${domainId}/topics/${topicId}/subtopics`)
      .then((r) => r.data.subTopics);
  },

  getLLevelGuide(): Promise<LLevelGuide> {
    return apiClient.get<LLevelGuide>('/api/v1/articles/l-level-guide').then((r) => r.data);
  },

  // My articles — all statuses for the current user
  listMyArticles(authorAlias: string): Promise<{ articles: MyArticle[]; count: number }> {
    return apiClient
      .get<{ articles: MyArticle[]; count: number }>('/api/v1/articles', {
        params: { author: authorAlias, limit: 100, sortBy: 'lastModifiedDate', sortOrder: 'desc' },
      })
      .then((r) => r.data);
  },
};

export interface MyArticle {
  articleId: string;
  id?: string;
  title: string;
  summary: string;
  status: string;
  validationStatus?: string;
  complexityLevel: ComplexityLevel;
  contentType?: ContentType;
  domainTags?: string[];
  createdDate: string;
  lastModifiedDate: string;
}
